import axios from 'axios';
import axiosInstance from './axiosConfig';
import { getDomain } from './constants';
import { convertDateFormat, formatDateToYYYYMMDD, getCookie, localStorageUpdateAfterLogin, RetryConfig, SignUpFormType, UserEligibilityType, withRetry } from './utils';


// API's

export const loginApi = async (username: string, password: string) => {
    const response = await axiosInstance.post('/auth/login', {
      username,
      password,
    });
    return response.data;
  };
  
  export const isValidSignUpFormData = (form: SignUpFormType, theme?: any) => {
  if (!form.firstName || !(form.firstName?.length > 2)) {
    return 'Please enter first name';
  }
  if (!form.lastName || !(form.lastName?.length > 2)) {
    return 'Please enter last name';
  }
  if (!form.userName || !(form.userName?.length > 2)) {
    return 'Please enter username';
  }
  if (!form.email || !form.email.includes('@')) {
    return 'Please enter valid email';
  }
  if (!form.password || !(form.password?.length > 8)) {
    return 'Please enter a password with at least 8 characters';
  }
  if (form.password !== form.confirmPassword) {
    return 'Passwords do not match';
  }
  if (!form.phoneNumber || !form.phoneNumber?.match(/^[0-9]{10}$/)) {
    return 'Please enter a valid phone number';
  }

  // Only validate optional fields if theme is loaded and the field is enabled
  if (theme && theme.showDobField && (!form.dateOfBirth || form.dateOfBirth.trim() === '')) {
    return 'Please enter date of birth';
  }

  if (theme && theme.showSsnField && (!form.uniqueCustomerIdentifier || !form.uniqueCustomerIdentifier.match(/^[0-9]{4}$/))) {
    return 'Please enter last 4 digits of SSN';
  }

  if (theme && theme.showGarantorNumberField && (!form.guarantorNumber || form.guarantorNumber.trim() === '')) {
    return 'Please enter guarantor number';
  }

  if (theme && theme.showGarantorLastNameField && (!form.guarantorLastName || form.guarantorLastName.trim() === '')) {
    return 'Please enter guarantor last name';
  }

  return null;
};
  
  export const signUpApi = async (form: SignUpFormType, theme?: any) => {
  const payload: any = {
    firstName: form.firstName,
    lastName: form.lastName,
    username: form.userName,
    email: form.email,
    password: form.password,
    phone: form.phoneNumber,
  };

  // Only add optional fields if they are enabled in the theme and have values
  if (theme?.showDobField && form.dateOfBirth) {
    payload.dateOfBirth = form.dateOfBirth;
  }

  if (theme?.showGarantorNumberField && form.guarantorNumber) {
    payload.guarantorNumber = form.guarantorNumber;
  }

  if (theme?.showGarantorLastNameField && form.guarantorLastName) {
    payload.guarantorLastName = form.guarantorLastName;
  }

  // For SSN field, we'll use uniqueCustomerIdentifier
  if (theme?.showSsnField && form.uniqueCustomerIdentifier) {
    payload.uniqueCustomerIdentifier = form.uniqueCustomerIdentifier;
  }

  const response = await axiosInstance.post('/auth/signup', payload);
  return response.data;
};
  
  export const postUserEligibilityData = async (form: UserEligibilityType) => {
    const response = await axiosInstance.post('/user_eligibility_data/create', {
      membershipId: form.membershipId,
      insuranceCompanyName: form.insuranceCompanyName,
      primaryInsuranceSubscriber: form.primaryInsuranceSubscriber,
      userDob: form.dateOfBirth,
    });
    return response.data;
  };
  
  export const getPayers = async () => {
    try {
      const response = await axiosInstance.get("/payers");
      return response.data;
    } catch (error) {
      console.error("Error fetching payers:", error);
      throw error;
    }
  };
  
  export const confirmSignUpOtp = async (username: string, otp: string) => {
    try {
      const response = await axiosInstance.post(
        `/auth/confirm?username=${username}&confirmationCode=${otp}`
      );
      return response.data;
    } catch (error) {
      console.error("Error signing up:", error);
      throw error;
    }
  };
  

  export const getPendingTransactions = async (guarantorLastName?: string, guarantorNumber?: string, paymentId?: string) => {
    try {
      let url = '/public/api/payments/patient-accounts?';
      const params = new URLSearchParams();
      
      if (guarantorLastName) {
        params.append('guarantorLastName', guarantorLastName);
      }
      if (guarantorNumber) {
        params.append('guarantorNumber', guarantorNumber);
      }
      if (paymentId) {
        params.append('paymentId', paymentId);
      }
      
      url += params.toString();
      const response = await axiosInstance.get(url);
      console.log("Response from patient accounts:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching patient accounts:", error);
      throw error;
    }
  };

  export const getPatientTransactions = async (accountId: number) => {
    try {
      const response = await axiosInstance.get(`/api/patient-transactions/${accountId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching patient transactions:", error);
      throw error;
    }
  };
  
  export const updatePostPayment = async (amount : number = 0, referenceId: string, payabaliResponse: any, paymentIdentityId: string, isScheduled: boolean = false, scheduledStartDate?: string, frequency?: string, paymentType?: string, firstName?: string, lastName?: string, email?: string, metadata?: any, retryConfig?: RetryConfig) => {
    interface PaymentPayload {
      paymentIdentityId: string;
      paymentType: string;
      amount: number;
      status: string;
      paymentAggregatorResponse: {
        referenceId: string;
        resultCode: number;
        resultText: string;
        customerId: string;
        methodReferenceId: string;
      };
      paymentReferenceId: string;
      responseData: any;
      scheduledStartDate?: string;
      frequency?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      metadata?: any;
    }
    
    // Prepare the request payload
    const payload: PaymentPayload = {
      paymentIdentityId: paymentIdentityId || '',
      paymentType: paymentType || (isScheduled ? "RECURRING" : "ONE_TIME"),
      amount: amount,
      status: payabaliResponse.isSuccess ? "SUCCESS" : "FAILED",
      paymentAggregatorResponse: {
        referenceId: payabaliResponse?.responseData?.referenceId || '',
        resultCode: payabaliResponse?.responseData?.resultCode || 1,
        resultText: payabaliResponse?.responseData?.resultText || "Added",
        customerId: payabaliResponse?.responseData?.customerId || '',
        methodReferenceId: payabaliResponse?.responseData?.methodReferenceId || payabaliResponse?.responseData?.referenceId || ''
      },
      scheduledStartDate: scheduledStartDate || new Date().toISOString().split('T')[0],
      frequency: frequency || 'ONETIME',
      paymentReferenceId: referenceId,
      responseData: payabaliResponse.responseData || {},
      firstName: firstName,
      lastName: lastName,
      email: email,
      metadata: metadata
    };
    
    // Add scheduled payment fields if this is a scheduled payment
    if (isScheduled && scheduledStartDate && frequency) {
      payload.scheduledStartDate = scheduledStartDate;
      payload.frequency = frequency.toUpperCase();
    }
    
    const response = await axiosInstance.post("/public/api/payments/process-transaction-and-update-payment", payload);
    return response.data;
  };
  

  export const updatePatientPostPayment = async (amount : number = 0, referenceId: string, payabaliResponse: any, isScheduled: boolean = false, scheduledStartDate?: string, frequency?: string, guarantorLastName?: string, guarantorNumber?: string) => {
    interface PaymentPayload {
      paymentType: string;
      amount: number;
      status: string;
      paymentAggregatorResponse: {
        referenceId: string;
        resultCode: number;
        resultText: string;
        customerId: string;
        methodReferenceId: string;
      };
      paymentReferenceId: string;
      responseData: any;
      scheduledStartDate?: string;
      frequency?: string;
      metadata?: any;
      guarantorLastName?: string;
      guarantorNumber?: string;
    }
    
    // Prepare the request payload
    const payload: PaymentPayload = {
      paymentType: (isScheduled ? "RECURRING" : "ONE_TIME"),
      amount: amount,
      status: payabaliResponse.isSuccess ? "SUCCESS" : "FAILED",
      paymentAggregatorResponse: {
        referenceId: payabaliResponse?.responseData?.referenceId || '',
        resultCode: payabaliResponse?.responseData?.resultCode || 1,
        resultText: payabaliResponse?.responseData?.resultText || "Added",
        customerId: payabaliResponse?.responseData?.customerId || '',
        methodReferenceId: payabaliResponse?.responseData?.methodReferenceId || payabaliResponse?.responseData?.referenceId || ''
      },
      scheduledStartDate: scheduledStartDate || new Date().toISOString().split('T')[0],
      frequency: frequency || 'ONETIME',
      paymentReferenceId: referenceId,
      responseData: payabaliResponse.responseData || {},
      guarantorLastName: guarantorLastName,
      guarantorNumber: guarantorNumber
    };
    
    // Add scheduled payment fields if this is a scheduled payment
    if (isScheduled && scheduledStartDate && frequency) {
      payload.scheduledStartDate = scheduledStartDate;
      payload.frequency = frequency.toUpperCase();
    }
    
    const response = await axiosInstance.post("/public/api/payments/process-patient-transaction-and-update-payment", payload);
    return response.data;
  };
  
  export const AddNewUserSessionId = async (username: string, sessionId: string) => {
    try {
      const response = await axiosInstance.post("/api/sessions", {
        userId: localStorage.getItem("userid"),
        sessionId: sessionId,
      });
      return response.data;
    } catch (error) {
      console.error("Error in Add New User Session API:", error);
      throw error;
    }
  };
  
  export const changePasswordApi = async (oldPassword: string, newPassword: string) => {
    try {
      const response = await axiosInstance.post("/auth/changepassword", {
        accessToken: getCookie("Authorized"),
        username: localStorage.getItem("username"),
        oldPassword: oldPassword,
        newPassword: newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Error in Change Password API:", error);
      return null;
    }
  };
  
  export const sendInsuranceData = async (formData: any) => {
    try {
      const response = await axiosInstance.post("/auth/ovum/user", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        membershipId: formData.insuranceMemberId,
        insuranceCompanyName: formData.insuranceCompany,
        insuranceExist: !!formData.isInsuranceExist,
        userDob: formData.dob && formatDateToYYYYMMDD(formData.dob),
        state: formData.state || "",
      });
  
      localStorageUpdateAfterLogin(response?.data);
      return response;
    } catch (error) {
      console.error("Error in sendInsuranceData API:", error);
      throw error;
    }
  };
  
  export const getMembershipEligibilityData = async (state: string, provider: string, dob: string, membership: string) => {
    try {
      const response = await axiosInstance.post("/membership_eligibility/validate", {
        providerNPIID: "1205536703",
        providerLastName: "ovum",
        location: "",
        state,
        insuranceProvider: provider,
        dob,
        membershipId: membership
      });
  
      // Add some logic for processing eligibility data if needed
      return response;
    } catch (error) {
      console.error("Error in getMembershipEligibilityData API:", error);
      return null;
    }
  };
  
  export const fetchBillsByStatus = async (status?: string) => {
    try {
      const url = status ? `/api/receipts?status=${status}` : `/api/receipts`;
      const response = await axiosInstance.get(url);
  
      const receiptsByProvider = response.data?.receiptsByProvider || [];
      const receiptsByProviderWithType = receiptsByProvider.map((receipt: any) => ({
        ...receipt,
        type: "Provider",
        statementDate: convertDateFormat(receipt.statementDate),
        dueDateUnformatted: receipt.dueDate,
        dueDate: convertDateFormat(receipt.dueDate),
        paymentDate: status ? convertDateFormat(receipt.updatedAt) : undefined,
        isPaid: !!status,
      }));
  
      return { receiptsByProvider: receiptsByProviderWithType };
    } catch (error) {
      console.error("Error fetching bills:", error);
      throw error;
    }
  };
  
  export const getPayabliTokens = async (providerId: string) => {
    try {
      const response = await axiosInstance.get(`/public/api/payments/get-donation-token`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Payabli tokens:", error);
      throw error;
    }
  };
  
  export const postPaymentForLoginPayabli = async (
    paymentId: string,
    status: string,
    receiptId: string
  ) => {
    try {
      const response = await axiosInstance.post("/api/post-payment", {
        paymentAggregator: "payabli",
        paymentIdByAggregator: paymentId,
        receiptType: "provider",
        status: status,
        receiptId: receiptId,
      });
  
      return response;
    } catch (error) {
      console.error("Error posting payment for Payabli login:", error);
      return null;
    }
  };

  export const loggerAPI = async (data: any) => {
    try {
      await axios.post(getDomain() +"/public/logger", data);
    } catch (error) {
      // If this API fails, we can log the error to the console, but we don't want to throw an error
      console.error("Error in loggerAPI:", error);
    }
  }

// Subscription APIs
export const fetchSubscriptionsApi = async () => {
  try {
    const response = await axiosInstance.get('/api/v1/subscriptions');
    return response.data;
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    throw error;
  }
};

export const pauseSubscriptionApi = async (subscriptionId: string) => {
  try {
    const response = await axiosInstance.post(`/api/v1/subscriptions/${subscriptionId}/pause`);
    return response.data;
  } catch (error) {
    console.error("Error pausing subscription:", error);
    throw error;
  }
};

export const resumeSubscriptionApi = async (subscriptionId: string) => {
  try {
    const response = await axiosInstance.post(`/api/v1/subscriptions/${subscriptionId}/resume`);
    return response.data;
  } catch (error) {
    console.error("Error resuming subscription:", error);
    throw error;
  }
};

export const cancelSubscriptionApi = async (subscriptionId: string) => {
  try {
    const response = await axiosInstance.delete(`/api/v1/subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error) {
    console.error("Error cancelling subscription:", error);
    throw error;
  }
};

export const getPaymentHistoryApi = async (page: number = 0, size: number = 20) => {
  try {
    const response = await axiosInstance.get(`/api/v2/get-payment-history?page=${page}&size=${size}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching payment history:", error);
    throw error;
  }
};

export const checkPatientStatus = async (patientData: {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone?: string;
  email?: string;
}) => {
  try {
    const response = await axiosInstance.post('/patients/status', patientData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking patient status:', error);
    throw error;
  }
};

// Get open appointment slots from Athena
export const getOpenSlots = async (requestData: {
  reasonType: 'existing' | 'telehealth' | 'new';
  reason: string;
  startDate: string;
  endDate?: string;
  zipCode?: string;
  radiusMiles?: number;
  providerName?: string;
}) => {
  try {
    const response = await axiosInstance.post('/api/appointments/athena/open-slots', requestData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching open slots:', error);
    throw error;
  }
};

// Book appointment with Athena
export const bookAppointment = async (appointmentData: {
  appointmentId: string;
  departmentId: string;
  providerId: string;
  patientId?: string;
  appointmentTypeId: string;
  reason: string;
  reasonType: 'existing' | 'new';
  insuranceCompany: string;
  insuranceIdNumber: string;
  insuranceNote?: string;
  // Appointment date and time
  appointmentDate?: string;
  appointmentTime?: string;
  // For new patients
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  sex?: string;
  phone?: string;
  phoneType?: 'mobile' | 'home';
  email?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
}) => {
  try {
    const payload = { ...appointmentData } as any;

    // normalizeDob not used; keeping here if needed in future

    if (payload.dateOfBirth && typeof payload.dateOfBirth === 'string') {
      const raw = payload.dateOfBirth.trim();
      // If user provided MM/DD/YYYY, pass it through unchanged
      if (raw.includes('/')) {
        payload.dateOfBirth = raw;
      } else {
        // Otherwise, leave as-is and allow backend to validate; do not over-convert
        payload.dateOfBirth = raw;
      }
    }

    const response = await axiosInstance.post('/api/appointments/athena/book', payload, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error booking appointment:', error);
    throw error;
  }
};

export const bookBulkAppointments = async (appointmentData: {
  patientId: string;
  reasonType: 'existing' | 'new';
  appointmentTypeId: string;
  reason: string;
  insuranceCompany?: string;
  insuranceIdNumber?: string;
  insuranceNote?: string;
  referralSource?: string;
  reasonForVisit?: string;
  items: Array<{
    appointmentId: string;
    departmentId: string;
    providerId: string;
    appointmentDate: string;
    appointmentTime: string;
  }>;
}) => {
  try {
    const response = await axiosInstance.post('/api/appointments/athena/book/bulk', appointmentData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error booking bulk appointments:', error);
    throw error;
  }
};

// Cancel appointment with Athena
export const cancelAppointment = async (appointmentData: {
  appointmentId: string;
  departmentId?: string;
  providerId?: string;
  patientId: string; // Make patientId required
  reason: string;
}) => {
  try {
    const response = await axiosInstance.put('/api/appointments/athena/cancel', appointmentData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error canceling appointment:', error);
    throw error;
  }
};

// Reschedule appointment with Athena
export const rescheduleAppointment = async (appointmentData: {
  appointmentId: string;
  targetAppointmentId: string;
  departmentId?: string;
  providerId?: string;
  patientId?: string;
  bookingNote?: string;
}) => {
  try {
    const response = await axiosInstance.put('/api/appointments/athena/reschedule', appointmentData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error rescheduling appointment:', error);
    throw error;
  }
};

// Get existing appointments for a patient
export const getPatientAppointments = async (patientId: string, status?: string) => {
  try {
    const queryParams = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await axiosInstance.get(`/api/appointments/athena/patients/${patientId}/appointments${queryParams}`, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    // Return the appointments array from the response
    return response.data.appointments || [];
  } catch (error) {
    console.error('Error fetching patient appointments:', error);
    throw error;
  }
};

//Get provider-specific open slots by provider ID or scheduling name (auto-detects)
export const getProviderSpecificOpenSlots = async (
  providerIdentifier: string,
  params: {
    // Direct pattern parameters (recommended for rescheduling)
    departmentId?: string;
    appointmentTypeId?: string;
    patientId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    // Legacy reason-based pattern parameters
    reasonType?: string;
    reason?: string;
    zipCode?: string;
    radiusMiles?: number;
  }
) => {
  try {
    const isNumericId = /^\d+$/.test(providerIdentifier);
    
    console.log('🔍 getProviderSpecificOpenSlots called:', {
      providerIdentifier,
      isNumericId,
      params
    });
    
    // Use direct pattern if departmentId and appointmentTypeId are provided
    const useDirectPattern = params.departmentId && params.appointmentTypeId;
    
    const queryParams = new URLSearchParams();
    
    if (useDirectPattern) {
      // Direct Athena API Call pattern (Pattern 1)
      queryParams.append('departmentId', params.departmentId!);
      queryParams.append('appointmentTypeId', params.appointmentTypeId!);
      if (params.patientId) queryParams.append('patientId', params.patientId);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.offset) queryParams.append('offset', String(params.offset));
    } else {
      // Reason-based mapping pattern (Pattern 2 - legacy)
      if (params.patientId) queryParams.append('patientId', params.patientId);
      if (params.reasonType) queryParams.append('reasonType', params.reasonType);
      if (params.reason) queryParams.append('reason', params.reason);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.zipCode) queryParams.append('zipCode', params.zipCode);
      if (params.radiusMiles) queryParams.append('radiusMiles', String(params.radiusMiles));
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.offset) queryParams.append('offset', String(params.offset));
    }

    const url = isNumericId
      ? `/api/appointments/athena/providers/${providerIdentifier}/open-slots?${queryParams}`
      : `/api/appointments/athena/providers/scheduling-name/${encodeURIComponent(providerIdentifier)}/open-slots?${queryParams}`;

    console.log('📞 Making provider-specific API call to:', url);

    const response = await axiosInstance.get(url, {
      headers: {
        Domain: 'axis',
        banner: 'axis',
        client: 'axis',
      },
    });
    
    console.log('✅ Provider-specific API response:', {
      providerIdentifier,
      responseLength: response.data?.length || 0,
      providers: response.data?.map((p: any) => ({ 
        id: p.providerId, 
        name: `${p.firstName} ${p.lastName}`,
        schedulingName: p.schedulingName 
      })) || []
    });
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching provider-specific open slots:', error);
    throw error;
  }
};

// Fetch providers filtered by appointment type and zip code
export const getProviders = async (appointmentType: 'telehealth' | 'inperson', zip?: string) => {
  try {
    const params: any = {
      type: appointmentType,
    };

    // Only include zip in params if it's provided and not empty
    if (zip && zip.trim() !== '') {
      params.zip = zip;
    }

    const response = await axiosInstance.get('/providers', {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching providers:', error);
    throw error;
  }
};
